import { useCallback } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { showWarning } from "src/utils/toast/toastUtil";
import { validateItemAccount, isDuplicateChildItemcd, restoreOriginalValues } from "../Util_MST3040_R01";
import { MST3040_R01DetailDto } from "../Dto_MST3040_R01";
import { ALLOWED_COORDINATE_ACCOUNTS, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import { getAfeederCodeList, getBfeederCodeList, getCfeederCodeList } from "src/apis/commonCodeApi";
import { createSearchFunctionFromCategory } from "src/utils/categoryUtils";

export function useCoordinateCellChange({
	MST3040_R01DetailList,
	updateMST3040_R01DetailList,
	originalMST3040_R01DetailList,
	settingCodeSearchModalProps,
	gridRef
}: any) {
	// 자품목코드 자동 검색 함수
	const handleChildItemCodeSearch = useCallback(
		async (itemCode: string, rowData: MST3040_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim()) {
				updateMST3040_R01DetailList(rowData.valueIndex!, {
					...rowData,
					childItemcd: '',
					childItemnm: '',
					itemSpec: '',
					locationCd: '',
					locationNm: '',
					isModified: rowData.inserted ? false : true,
				});
				return;
			}
			const originalData = {
				...rowData,
				childItemcd:		oldValue || rowData.childItemcd,
				childItemnm:		rowData.childItemnm,
				itemSpec:			rowData.itemSpec ?? '',
				locationCd:			rowData.locationCd ?? '',
				locationNm:			rowData.locationNm ?? '',
			};
			const searchFn = createSearchFunctionFromCategory("bom_childItemcd", {
				prntItemcd: rowData.prntItemcd || '',
			});
			if (!searchFn) {
				settingCodeSearchModalProps(
					'BOM_자품목',
					itemCode,
					false,
					'childItemcd',
					rowData,
					rowData.valueIndex!,
				);
				return;
			}
			try {
				const found = await searchFn(
					itemCode.trim().toUpperCase(),
					(item: any) => {
						updateMST3040_R01DetailList(rowData.valueIndex!, {
							...rowData,
							childItemcd: item.childItemcd || itemCode,
							childItemnm: item.itemNm || '',
							itemSpec: item.itemSpec || '',
							locationCd: item.locationCd || '',
							locationNm: item.locationNm || '',
							isModified: rowData.inserted ? false : true,
						});
					}
				);
				if (!found) {
					settingCodeSearchModalProps(
						'BOM_자품목',
						itemCode,
						false,
						'childItemcd',
						rowData,
						rowData.valueIndex!,
					);
				}
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateMST3040_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'BOM_자품목',
					itemCode ,
					false,
					'childItemcd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			MST3040_R01DetailList,
			updateMST3040_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleAfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: MST3040_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				afeederCd:		itemCode,
			};
			try {
				const response	= await getAfeederCodeList();
				settingCodeSearchModalProps(
					'Afeeder',
					itemCode,
					false,
					'afeederCd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateMST3040_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Afeeder',
					itemCode,
					false,
					'afeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			MST3040_R01DetailList,
			updateMST3040_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleBfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: MST3040_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				bfeederCd:		itemCode,
			};
		try {
			const response	= await getBfeederCodeList();
			settingCodeSearchModalProps(
				'Bfeeder',
				itemCode,
				false,
				'bfeederCd',
				rowData,
				rowData.valueIndex!,
			);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateMST3040_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Bfeeder',
					itemCode,
					false,
					'bfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			MST3040_R01DetailList,
			updateMST3040_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	const handleCfeederCodeSearch = useCallback(
		async (itemCode: string, rowData: MST3040_R01DetailDto, oldValue?: string) => {
			if (!itemCode || !itemCode.trim())			return;
			const originalData = {
				...rowData,
				cfeederCd:		itemCode,
			};
			try {
				const response	= await getCfeederCodeList();
				settingCodeSearchModalProps(
					'Cfeeder',
					itemCode,
					false,
					'cfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			} catch (error) {
				console.error('품목 검색 중 오류:', error);
				restoreOriginalValues(rowData.valueIndex!, originalData, updateMST3040_R01DetailList, gridRef);
				settingCodeSearchModalProps(
					'Cfeeder',
					itemCode,
					false,
					'cfeederCd',
					rowData,
					rowData.valueIndex!,
				);
			}
		},
		[
			MST3040_R01DetailList,
			updateMST3040_R01DetailList,
			settingCodeSearchModalProps,
			restoreOriginalValues,
			gridRef
		]
	);

	// 커스텀 셀 변경 핸들러
	const handleCellChange = useCallback(
		(event: any) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field)	return;
			const field = colDef.field as keyof MST3040_R01DetailDto;
			// 자품목코드/피더코드들은 코드검색(모달) 흐름이 있지만,
			// store에 먼저 반영하지 않으면 화면상 입력은 됐는데 리스트에는 값이 없는 상태가 될 수 있습니다.
			if (
				(field === 'childItemcd' ||
					field === 'afeederCd' ||
					field === 'bfeederCd' ||
					field === 'cfeederCd') &&
				newValue !== oldValue
			) {
				updateMST3040_R01DetailList(data.valueIndex!, {
					...data,
					[field]: newValue,
					isModified: data.inserted ? false : true,
				});

				if (field === 'childItemcd') {
					handleChildItemCodeSearch(newValue, { ...data, childItemcd: newValue }, oldValue);
				} else if (field === 'afeederCd') {
					handleAfeederCodeSearch(newValue, data, oldValue);
				} else if (field === 'bfeederCd') {
					handleBfeederCodeSearch(newValue, data, oldValue);
				} else if (field === 'cfeederCd') {
					handleCfeederCodeSearch(newValue, data, oldValue);
				}
				return;
			}
			
			const originalData =
				originalMST3040_R01DetailList.find(
					(item: MST3040_R01DetailDto) => item.valueIndex === data.valueIndex
				);
			const isModified =
				data.inserted === true
					? false
					: (originalData ? originalData[field] !== newValue : true);
			
			let updatedData = {
				...data,
				[field]: newValue,
				isModified: true
			};
			
			updateMST3040_R01DetailList(data.valueIndex!, updatedData);
		},
		[
			handleChildItemCodeSearch,
			handleAfeederCodeSearch,
			handleBfeederCodeSearch,
			handleCfeederCodeSearch,
			originalMST3040_R01DetailList,
			updateMST3040_R01DetailList
		]
	);

	return {
		handleCellChange,
		handleChildItemCodeSearch
	};
}